/* hs-eslint ignored failing-rules */
/* eslint-disable promise/catch-or-return */

'use es6';

import I18n from 'I18n';
import { Map as ImmutableMap, Set as ImmutableSet, List, OrderedSet, fromJS } from 'immutable';
import PropTypes from 'prop-types';
import { Component } from 'react';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Dropdown } from 'foundations-components/transitional/components/Dropdown';
import { FormLabel } from 'foundations-components/transitional/components/FormLabel';
import { AddIcon } from 'foundations-assets';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { List as FoundationsList } from 'foundations-components/transitional/components/List';
import { LoadingSpinner } from 'foundations-components/transitional/components/LoadingSpinner';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import * as FilterFamilyTranslator from 'customer-data-filters/components/FilterFamilyTranslator';
import DisplayValueTeam from 'customer-data-filters/components/display/DisplayValueTeam';
import FieldSelectItemComponent from 'customer-data-filters/components/editor/FieldSelectItemComponent';
import FilterOperatorTeamInput from 'customer-data-filters/components/operator/FilterOperatorTeamInput';
import * as FilterContactSearch from 'customer-data-filters/converters/contactSearch/FilterContactSearch';
import ConnectReferenceResolvers from 'reference-resolvers/ConnectReferenceResolvers';
import { CAMPAIGN, COMPANY, CONTACT, DEAL, DEAL_PIPELINE, DEAL_PIPELINE_STAGE, TEAM } from 'reference-resolvers/constants/ReferenceObjectTypes';
import * as dataTypes from 'reporting-data/constants/dataTypes';
import { DATA_TYPE_TO_HUBSPOT_OBJECT_COORDINATES } from 'reporting-data/constants/objectCoordinates';
import { HUBSPOT_CRM_OBJECTS_BY_ID, getCrmObjectName, isSupportedCrmObject } from 'reporting-data/crmObjects/utils';
import { isCustomObjectType } from 'reporting-data/lib/customObjects';
import getProperties from 'reporting-data/properties';
import { isBlocklistedForFiltering } from 'reporting-data/public/blocklisted-for-filtering';
import { getCurrencyInformation } from 'reporting-data/public/currency';
import { isExternalDataType } from 'reporting-data/report/externalReportGetters';
import { dataTypeSettings } from 'reporting-data/retrieve/inboundDb/common/dataTypes';
import { emptyTracker } from 'reporting-data/tsTypes/usageTrackerTypes';
import { UserInfoContext } from 'reporting-data/userInfo/userInfoContext';
import { WithUserInfoProvider } from 'reporting-ui-components/components/PlatformReport/UserInfoProvider';
import { getOperatorsForPropertyType } from 'reporting-ui-components/components/filterOperators/PropertyTypeToOperator';
import SelectSavedFilters from 'reporting-ui-components/components/shared/SelectSavedFilters';
import { ReportConfigActionTypes } from 'reporting-ui-components/constants/trackingConstants';
import events from 'reporting-ui-components/events.yaml';
import { EVENTS } from 'reporting-ui-components/lib/events';
import { getReportingSpecialOptionsByReferenceType } from 'reporting-ui-components/lib/getSpecialReportingOptionsByReferenceType';
import { RaiseLoading } from 'reporting-ui-components/trap/LoadingTrap';
import memoize from 'transmute/memoize';
import { Metrics } from './../monitoring/Metrics';
import { isFiscalYearEnabled as getIsFiscalYearEnabled } from './reportEditors/common/isFiscalYearEnabled';
import { Link } from 'foundations-components/transitional/components/Link';

// This allows us to support Webpack 3 and 4.
// Once all apps are on Webpack 4, this import can be simplified
// https://product.hubteam.com/docs/frontend/kb/webpack4.html
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getImport = imported => {
  if (imported.default) return imported.default;
  return imported;
};
const HUBSPOT_TEAM_OBJECTS = ImmutableSet([dataTypes.COMPANIES, dataTypes.CONTACTS, dataTypes.DEALS, dataTypes.TICKETS, dataTypes.ENGAGEMENT]);
const CUSTOMER_DATA_FILTERS_FISCAL_YEAR_ROLLING_OFFSET = '__hs__FISCAL_YEAR_OFFSET';
const getCurrencyInfo = memoize(() => getCurrencyInformation());
const dataTypeToObjectTypeId = dataType =>
// CRMObjects will have a mapping however analytics datatypes will not
DATA_TYPE_TO_HUBSPOT_OBJECT_COORDINATES.get(dataType) || dataType;
const appendFilters = (newFilters, existingFilters) => {
  const filters = OrderedSet(existingFilters);
  return List(filters.concat(newFilters));
};
const PROPERTY_RESOLVERS = {
  [dataTypes.CALL]: Object.assign({}, dataTypeSettings[dataTypes.CALL].propertyReferenceType),
  [dataTypes.CONTACTS]: Object.assign({}, dataTypeSettings[dataTypes.CONTACTS].propertyReferenceType),
  [dataTypes.COMPANIES]: Object.assign({}, dataTypeSettings[dataTypes.COMPANIES].propertyReferenceType),
  [dataTypes.CONVERSATIONS]: Object.assign({}, dataTypeSettings[dataTypes.CONVERSATIONS].propertyReferenceType),
  [dataTypes.DEALS]: Object.assign({}, dataTypeSettings[dataTypes.DEALS].propertyReferenceType),
  [dataTypes.COMMERCE_PAYMENTS]: Object.assign({}, dataTypeSettings[dataTypes.COMMERCE_PAYMENTS].propertyReferenceType),
  [dataTypes.ENGAGEMENT]: Object.assign({}, dataTypeSettings[dataTypes.ENGAGEMENT].propertyReferenceType, {
    'associations.contact': CONTACT,
    'associations.company': COMPANY,
    'associations.deal': DEAL
  }),
  [dataTypes.TICKETS]: Object.assign({}, dataTypeSettings[dataTypes.TICKETS].propertyReferenceType),
  [dataTypes.PRODUCTS]: {},
  [dataTypes.LINE_ITEMS]: Object.assign({}, dataTypeSettings[dataTypes.LINE_ITEMS].propertyReferenceType),
  [dataTypes.FEEDBACK_SUBMISSIONS]: Object.assign({}, dataTypeSettings[dataTypes.FEEDBACK_SUBMISSIONS].propertyReferenceType),
  [dataTypes.ATTRIBUTION_TOUCH_POINTS]: {
    hs_campaign_guid: CAMPAIGN,
    hs_deal_pipeline: DEAL_PIPELINE
  },
  [dataTypes.CONTACT_CREATE_ATTRIBUTION]: {
    hs_campaign_guid: CAMPAIGN
  },
  [dataTypes.DEAL_CREATE_ATTRIBUTION]: {
    hs_campaign_guid: CAMPAIGN,
    hs_deal_pipeline: DEAL_PIPELINE,
    hs_deal_stage: DEAL_PIPELINE_STAGE
  },
  [dataTypes.GOAL_TARGET]: {},
  [dataTypes.MEETING_EVENT]: {
    hubspot_team_id: TEAM
  },
  [dataTypes.TASK]: Object.assign({}, dataTypeSettings[dataTypes.TASK].propertyReferenceType)
};
const handleFilterChange = (logicGroup, onChange, isFiscalYearEnabled) => {
  let filters = FilterContactSearch.toContactSearch(logicGroup);
  if (isFiscalYearEnabled && filters) {
    filters = filters.map(filter => {
      if (filter.rollingOffset === CUSTOMER_DATA_FILTERS_FISCAL_YEAR_ROLLING_OFFSET) {
        filter.useFiscalYear = true;
      }
      return filter;
    });
  }
  onChange(fromJS(filters));
};
const getReferenceResolvers = ({
  dataType,
  fields,
  resolvers
}) => {
  let newReferences = {};
  if (isExternalDataType(dataType)) {
    // Find properties with external references
    const fieldsWithExternalReferences = fields.filter(field => field.get('referencedObjectType'));
    newReferences = fieldsWithExternalReferences.map(field => {
      const referencedObjectType = field.get('referencedObjectType');
      return resolvers && resolvers[referencedObjectType] || ImmutableMap({});
    }).toJS();
  } else {
    newReferences = ImmutableMap((dataTypeSettings[dataType] || {}).propertyReferenceType).map(property => {
      return resolvers && resolvers[property] || ImmutableMap({});
    }).toJS();
  }
  return Object.assign({}, resolvers, newReferences);
};
class ReportingFilterEditor extends Component {
  constructor(props) {
    super(props);
    this.state = {
      customObjectName: null,
      reportingProperties: ImmutableMap(),
      isEditing: false,
      chooseSavedFilters: false,
      actionsOpen: false,
      AsyncXOFilterEditor: () => /*#__PURE__*/_jsx(LoadingSpinner, {
        grow: true
      }),
      currencyInfo: {
        status: 'pending',
        value: {}
      },
      usageTracker: emptyTracker,
      trackingAssetType: 'report',
      trackingApplicationName: '',
      trackingScreenName: ''
    };
    this.fromContactSearch = filters => {
      const {
        fields,
        dataType,
        convertFromContactSearch
      } = this.props;
      if (convertFromContactSearch) {
        return convertFromContactSearch({
          fields,
          filters,
          dataType: dataTypeToObjectTypeId(dataType)
        });
      }
      return FilterContactSearch.fromContactSearch(fields, filters.toJS()).set('filterFamily', dataTypeToObjectTypeId(dataType));
    };
    this.getFieldDefinitions = () => {
      const {
        dataType,
        fieldDefinitions = ImmutableMap()
      } = this.props;
      if (HUBSPOT_TEAM_OBJECTS.contains(dataType) || isCustomObjectType(dataType)) {
        const key = 'hubspot_team_id';
        return fieldDefinitions.merge(ImmutableMap({
          [key]: {
            InputComponent: FilterOperatorTeamInput,
            ValueComponent: DisplayValueTeam,
            referencedObjectType: 'TEAM'
          }
        }));
      }
      return fieldDefinitions;
    };
    this.getReferenceResolvers = () => {
      return getReferenceResolvers(this.props);
    };

    // Note: Active means fields that have already been added to the filter's logic group
    this.getActiveFieldNames = () => {
      const {
        filters
      } = this.props;
      return () => {
        // set of fields that should only be added once
        const singletonFilters = ['filterId'];
        const fieldsThatHaveBeenUsedInFilter = singletonFilters.filter(field => filters.find(filter => filter.get('property') === field));
        return ImmutableSet(fieldsThatHaveBeenUsedInFilter);
      };
    };
    this.handleChange = logicGroup => {
      const {
        report,
        onChange
      } = this.props;
      const {
        gates
      } = this.context;
      handleFilterChange(logicGroup, onChange, getIsFiscalYearEnabled(report, gates));
      this.setState({
        isEditing: false
      });
    };
    this.handleEditingChange = ({
      target: {
        value: isEditing
      }
    }) => {
      this.setState({
        isEditing
      });
      if (this.props.onEditingChange) {
        this.props.onEditingChange(isEditing);
      }
    };
    this.handleOpenChooseSavedFilters = e => {
      this.setState({
        chooseSavedFilters: true,
        actionsOpen: true
      });
      e.stopPropagation();
      return;
    };
    this.handleAddSavedFilters = newFilters => {
      const {
        filters,
        onChange
      } = this.props;
      this.setState({
        chooseSavedFilters: false
      });
      if (newFilters) {
        onChange(appendFilters(fromJS(newFilters), filters));
      }
    };
    this.handleActionsOpenChange = ({
      target: {
        value: isOpen
      }
    }) => {
      this.setState(state => Object.assign({}, state, {
        actionsOpen: isOpen,
        chooseSavedFilters: state.chooseSavedFilters && isOpen
      }));
    };
    this.renderSelectSavedFilters = () => {
      const {
        dataType
      } = this.props;
      const {
        usageTracker
      } = this.state;
      if (!dataType) {
        return null;
      }
      return /*#__PURE__*/_jsx(SelectSavedFilters, {
        dataType: dataType,
        onDone: this.handleAddSavedFilters,
        usageTracker: usageTracker
      });
    };
    this.renderActions = () => {
      const {
        readOnly,
        canApplySavedFilters,
        hasBuilderAccess
      } = this.props;
      const {
        chooseSavedFilters,
        actionsOpen
      } = this.state;
      if (readOnly || !canApplySavedFilters) {
        return null;
      }
      const actionsList = /*#__PURE__*/_jsx(FoundationsList, {
        wrapChildren: true,
        children: /*#__PURE__*/_jsx(Tooltip, {
          title: /*#__PURE__*/_jsx(FormattedJSXMessage, {
            message: "reporting-ui-components.filterEditor.actions.addSavedFilters.disabledTooltip_jsx",
            elements: {
              UILink: Link
            },
            options: {
              external: true,
              href: 'https://www.hubspot.com/products/marketing',
              target: 'blank'
            }
          }),
          disabled: hasBuilderAccess,
          children: /*#__PURE__*/_jsx(Button, {
            use: "link",
            onClick: this.handleOpenChooseSavedFilters,
            disabled: !hasBuilderAccess,
            children: I18n.text('reporting-ui-components.filterEditor.actions.addSavedFilters.label')
          })
        })
      });
      return /*#__PURE__*/_jsx(Dropdown, {
        buttonUse: 'link',
        placement: 'bottom left',
        buttonText: I18n.text('reporting-ui-components.filterEditor.actions.label'),
        open: actionsOpen,
        onOpenChange: this.handleActionsOpenChange,
        closeOnOutsideClick: true,
        children: chooseSavedFilters ? this.renderSelectSavedFilters() : actionsList
      });
    };
    this.renderFiltersHeader = () => {
      const {
        headerLabel
      } = this.props;
      const {
        isEditing
      } = this.state;
      if (!isEditing) {
        return /*#__PURE__*/_jsxs(TFlex, {
          justify: "between",
          align: "center",
          children: [/*#__PURE__*/_jsx(FormLabel, {
            children: headerLabel
          }), this.renderActions()]
        });
      }
      return null;
    };
    this.getDisabledSelectItemKey = (dataType, reportingProperty) => {
      if (reportingProperty.get('name').includes('count')) {
        return I18n.text('reporting-ui-components.filterEditor.disabledProperty.countPropertiesNotSupportedForFiltering');
      }
      return I18n.text('reporting-ui-components.filterEditor.disabledProperty.notSupportedForFiltering');
    };
    this.renderFieldSelectItemComponent = selectItem => {
      const {
        reportingProperties
      } = this.state;
      const {
        dataType
      } = this.props;
      const reportingProperty = reportingProperties.get(selectItem.field.get('name'));
      return reportingProperty && isBlocklistedForFiltering(dataType, false, reportingProperty) ? /*#__PURE__*/_jsx(Tooltip, {
        title: this.getDisabledSelectItemKey(dataType, reportingProperty),
        children: /*#__PURE__*/_jsx(FieldSelectItemComponent, Object.assign({}, selectItem, {
          disabled: true
        }))
      }) : /*#__PURE__*/_jsx(FieldSelectItemComponent, Object.assign({}, selectItem));
    };
    this.getFilterFamilyObjectName = filterFamily => {
      const {
        dataType
      } = this.props;
      const {
        customObjectName
      } = this.state;
      if (isSupportedCrmObject(dataType)) {
        return customObjectName;
      }
      return FilterFamilyTranslator.LegacyFilterFamilyObjectNameTranslator(filterFamily);
    };
    this.trackFilterAction = filterUserActionRecord => {
      const {
        usageTracker,
        trackingApplicationName,
        trackingAssetType,
        trackingScreenName
      } = this.state;
      const {
        condition,
        subAction
      } = filterUserActionRecord;
      if (Object.prototype.hasOwnProperty.call(ReportConfigActionTypes, subAction)) {
        usageTracker.track(EVENTS.changeSrvReportConfigFilters, {
          action: ReportConfigActionTypes[subAction],
          filterType: condition,
          assetType: trackingAssetType || 'report',
          applicationName: trackingApplicationName,
          whereScreen: trackingScreenName
        });
      }
    };
    this.getOperatorsForType = type => {
      const {
        getOperatorsForType
      } = this.props;
      const {
        gates
      } = this.context;
      return getOperatorsForType(type, gates);
    };
  }
  componentDidMount() {
    const {
      dataType,
      appUsageTracker,
      trackingAssetType,
      trackingApplicationName,
      trackingScreenName
    } = this.props;
    import( /* webpackChunkName: "reporting-ui-components__XOFilterEditor" */
    'customer-data-filters/components/XOFilterEditor').then(getImport).then(AsyncXOFilterEditor => {
      this.setState({
        AsyncXOFilterEditor
      });
    });

    // get properties
    getProperties(dataType).then(properties => this.setState({
      reportingProperties: properties.get(dataType)
    }));
    getCurrencyInfo().then(currencyResponse => this.setState({
      currencyInfo: {
        status: 'resolved',
        value: currencyResponse
      }
    }));

    // get custom object name
    if (isSupportedCrmObject(dataType)) {
      getCrmObjectName(dataType).then(customObjectName => this.setState({
        customObjectName
      }));
    }
    // set usage tracker
    if (appUsageTracker) {
      this.setState({
        usageTracker: appUsageTracker.clone({
          events,
          preserveTrackerProperties: false
        }),
        trackingApplicationName,
        trackingAssetType,
        trackingScreenName
      });
    }
    Metrics.counter('legacy-filter-editor-usage').increment();
  }
  renderFilterEditor() {
    const {
      dataType,
      fields,
      fieldGroups,
      filters,
      readOnly,
      report,
      getFilterFamilyName
    } = this.props;
    const {
      AsyncXOFilterEditor,
      isEditing,
      currencyInfo
    } = this.state;
    const {
      gates
    } = this.context;
    const {
      value: {
        homeCurrency: {
          currencyCode = 'USD'
        }
      }
    } = currencyInfo;
    const value = this.fromContactSearch(filters);
    const isEmptyState = value.get('conditions', List()).isEmpty();
    const filterFamily = DATA_TYPE_TO_HUBSPOT_OBJECT_COORDINATES.get(dataType) || dataType;
    if (isEmptyState && !isEditing) {
      return /*#__PURE__*/_jsxs(Button, {
        disabled: readOnly,
        block: true,
        use: "link",
        onClick: () => {
          this.setState({
            isEditing: true
          });
        },
        "data-selenium-test": "FilterEditorOperatorCreateButton-filters2",
        paddingBlockEnd: "300",
        paddingBlockStart: "200",
        children: [/*#__PURE__*/_jsx(AddIcon, {}), /*#__PURE__*/_jsx(FormattedMessage, {
          message: "filterSidebar.addFilter"
        }, "text")]
      });
    }
    return /*#__PURE__*/_jsx(AsyncXOFilterEditor, {
      style: {
        minHeight: '300px'
      },
      baseFilterFamily: filterFamily,
      filterFamily: filterFamily,
      isFiscalYearEnabled: getIsFiscalYearEnabled(report, gates),
      getFieldDefinitions: this.getFieldDefinitions,
      getFieldGroups: () => fieldGroups && fieldGroups.map(fieldGroup => fieldGroup.update('properties', properties => properties.filter(property => !property.get('hidden')))),
      getFields: () => fields,
      getOperatorsForType: this.getOperatorsForType,
      isXoEnabled: true,
      readOnly: readOnly,
      getSpecialFieldOptions: getReportingSpecialOptionsByReferenceType,
      onEditingChange: this.handleEditingChange,
      currencyCode: currencyCode,
      getFilterFamilyOptions: () => List.of(filterFamily),
      getReferenceType: (_, propertyName) => {
        const dataTypeForReference = HUBSPOT_CRM_OBJECTS_BY_ID.get(dataType, dataType);
        return (PROPERTY_RESOLVERS[dataTypeForReference] || {})[propertyName];
      },
      getReferenceResolvers: this.getReferenceResolvers,
      value: value,
      onChange: this.handleChange,
      onUserAction: this.trackFilterAction,
      getFilterFamilyName: getFilterFamilyName,
      getFilterFamilyObjectName: this.getFilterFamilyObjectName,
      isFieldDescriptionVisible: true,
      isInitialScreenCreate: isEmptyState,
      getActiveFieldNames: this.getActiveFieldNames,
      showInitialStepBackButton: true,
      FieldSelectItemComponent: this.renderFieldSelectItemComponent
    });
  }
  render() {
    const {
      currencyInfo
    } = this.state;
    if (currencyInfo.status === 'pending') {
      return /*#__PURE__*/_jsx(RaiseLoading, {});
    }
    return /*#__PURE__*/_jsxs(TFlex, {
      direction: "column",
      children: [this.renderFiltersHeader(), this.renderFilterEditor()]
    });
  }
}
ReportingFilterEditor.contextType = UserInfoContext;
ReportingFilterEditor.defaultProps = {
  fieldDefinitions: ImmutableMap(),
  getOperatorsForType: getOperatorsForPropertyType,
  hasBuilderAccess: true,
  readOnly: false,
  report: ImmutableMap()
};
ReportingFilterEditor.propTypes = {
  canApplySavedFilters: PropTypes.bool.isRequired,
  dataType: PropTypes.string,
  filters: PropTypes.instanceOf(List).isRequired,
  fieldDefinitions: PropTypes.instanceOf(ImmutableMap).isRequired,
  fieldGroups: PropTypes.object.isRequired,
  fields: PropTypes.object.isRequired,
  headerLabel: PropTypes.node,
  onChange: PropTypes.func.isRequired,
  appUsageTracker: PropTypes.object,
  onEditingChange: PropTypes.func,
  currencyInfo: PropTypes.object,
  readOnly: PropTypes.bool.isRequired,
  resolvers: PropTypes.object,
  getOperatorsForType: PropTypes.func,
  convertFromContactSearch: PropTypes.func,
  getFilterFamilyName: PropTypes.func,
  hasBuilderAccess: PropTypes.bool,
  report: PropTypes.instanceOf(ImmutableMap),
  trackingAssetType: PropTypes.string,
  trackingApplicationName: PropTypes.string,
  trackingScreenName: PropTypes.string
};
const getResolvers = ConnectReferenceResolvers(resolvers => ({
  resolvers
}));
export default getResolvers(WithUserInfoProvider(ReportingFilterEditor));
export const __TESTABLE__ = {
  handleFilterChange,
  getReferenceResolvers
};